import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import JobService from "../../../../services/JobService";
import JobActivityService from "../../../../services/JobActivityService";
import { colors } from "../../../config/colors";
import JobDetails from './JobDetails';
import JobUpdates from './JobUpdates';
import JobChanges from './JobChanges';
import { getJobTitleLabel } from "../../../utils/jobTitleLabel";

const ViewJob = () => {
  const jobLabel = getJobTitleLabel();
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [jobActivity, setJobActivity] = useState(null);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    fetchJobDetails();
    fetchJobActivity();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await JobService.getJobById(jobId);
      if (response.data.success) {
        let job = response.data.data;
        let startTime = job.scheduledStartTime;
        if (startTime && startTime.includes('T')) {
          const startDate = new Date(startTime);
          startTime = startDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }

        // Format end time
        let endTime = job.scheduledEndTime;
        if (endTime && endTime.includes('T')) {
          const endDate = new Date(endTime);
          endTime = endDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }

        job.scheduledStartTime = startTime;
        job.scheduledEndTime = endTime;
        setJob(job);
      } else {
        throw new Error(`Failed to fetch ${jobLabel.toLowerCase()} details`);
      }
    } catch (error) {
      console.error(`Error fetching ${jobLabel.toLowerCase()} details:`, error);
      toast.error(`Failed to fetch ${jobLabel.toLowerCase()} details`);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobActivity = async () => {
    try {
      const response = await JobActivityService.getJobActivityByJobId(jobId);
      if (response.success) {
        setJobActivity(response.data);
        setActiveTab("changes");
      }
    } catch (error) {
      console.error("Error fetching job activity:", error);
      setJobActivity(null);
      // toast.error("Failed to fetch job activity");
    }
  };

  const handleJobApproval = async (approve) => {
    try {
      const response = await JobActivityService.approveJobActivity(jobActivity._id, approve);
      if (response.success) {
        toast.success(`${jobLabel} updated successfully`);
        setJobActivity(null);
        setActiveTab("details");
      } else {
        toast.error(`Failed to update ${jobLabel.toLowerCase()}`);
      }
    } catch (error) {
      console.error(`Error updating ${jobLabel.toLowerCase()}:`, error);
      toast.error(`Failed to update ${jobLabel.toLowerCase()}`);
    }
  };

  const handleClose = () => {
    navigate("/job-list");
  };

  const handleArchiveJob = async () => {
    try {
      setArchiving(true);
      const response = await JobService.archiveJob(job._id, true);
      if (response.success) {
        toast.success(`${jobLabel} archived successfully`);
        fetchJobDetails();
      } else {
        throw new Error(`Failed to archive ${jobLabel.toLowerCase()}`);
      }
    } catch (error) {
      console.error(`Error archiving ${jobLabel.toLowerCase()}:`, error);

      // Handle API error response with detailed message
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to archive ${jobLabel.toLowerCase()}`);
      }
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-gray-300 rounded-full animate-spin" style={{ borderTopColor: colors.primary.headingsBody }}></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {jobLabel} not found</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 py-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab("details")}
              className={`${activeTab === "details"
                  ? "border-primary-600 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              style={{
                borderColor: activeTab === "details" ? colors.primary.headingsBody : undefined,
                color: activeTab === "details" ? colors.primary.headingsBody : undefined
              }}
            >
              {`${jobLabel} Details`}
            </button>
            <button
              onClick={() => setActiveTab("updates")}
              className={`${activeTab === "updates"
                  ? "border-primary-600 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              style={{
                borderColor: activeTab === "updates" ? colors.primary.headingsBody : undefined,
                color: activeTab === "updates" ? colors.primary.headingsBody : undefined
              }}
            >
              Employee Updates
            </button>
            {jobActivity && (
              <button
                onClick={() => setActiveTab("changes")}
                className={`${activeTab === "changes"
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                style={{
                  borderColor: activeTab === "changes" ? colors.primary.headingsBody : undefined,
                  color: activeTab === "changes" ? colors.primary.headingsBody : undefined
                }}
              >
                Pending Changes
              </button>
            )}
          </nav>
        </div>
        <div className="flex gap-2">
          {!job.approved &&
            <button
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
              style={{ backgroundColor: colors.success.dark }}
              onClick={async () => {
                try {
                  await JobService.approveJob(job._id);
                  toast.success(`${jobLabel} approved successfully`);
                  fetchJobDetails();
                } catch (error) {
                  console.error(`Error approving ${jobLabel.toLowerCase()}:`, error);
                  toast.error(`Failed to approve ${jobLabel.toLowerCase()}`);
                }
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">{`Approve ${jobLabel}`}</span>
            </button>
          }
          {job && (
            <button
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
              style={{ backgroundColor: colors.highlights.callToAction }}
              onClick={() => navigate(`/edit-job/${job._id}`)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              <span className="text-sm">{`Edit ${jobLabel}`}</span>
            </button>
          )}
          {!job.isArchived && (
            <button
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
              style={{ backgroundColor: colors?.warning?.dark || '#f59e0b' }}
              onClick={handleArchiveJob}
              disabled={archiving}
            >
              {archiving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                </svg>
              )}
              <span className="text-sm">{archiving ? 'Archiving...' : `Archive ${jobLabel}`}</span>
            </button>
          )}
          {job.isArchived && (
            <button
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
              style={{ backgroundColor: colors.success.dark }}
              onClick={async () => {
                try {
                  setArchiving(true);
                  await JobService.archiveJob(job._id, false);
                  toast.success(`${jobLabel} unarchived successfully`);
                  fetchJobDetails();
                } catch (error) {
                  console.error(`Error unarchiving ${jobLabel.toLowerCase()}:`, error);

                  // Handle API error response with detailed message
                  if (error.response?.data?.error) {
                    toast.error(error.response.data.error);
                  } else if (error.response?.data?.message) {
                    toast.error(error.response.data.message);
                  } else {
                    toast.error(`Failed to unarchive ${jobLabel.toLowerCase()}`);
                  }
                } finally {
                  setArchiving(false);
                }
              }}
              disabled={archiving}
            >
              {archiving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              )}
              <span className="text-sm">{archiving ? 'Unarchiving...' : `Unarchive ${jobLabel}`}</span>
            </button>
          )}
          {jobActivity && activeTab === "changes" && (
            <>
              <button
                className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
                style={{ backgroundColor: colors.success.dark }}
                onClick={() => handleJobApproval(true)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                <span className="text-sm">Approve Changes</span>
              </button>
              <button
                className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
                style={{ backgroundColor: colors.error.dark }}
                onClick={() => handleJobApproval(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                <span className="text-sm">Reject Changes</span>
              </button>
            </>
          )}
          <button
            className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
            style={{ backgroundColor: colors.highlights.callToAction }}
            onClick={handleClose}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            <span className="text-sm">Back to List</span>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {activeTab === "details" && (
          <JobDetails job={job} onJobUpdated={fetchJobDetails} />
        )}

        {activeTab === "updates" && (
          <JobUpdates job={job} onJobUpdated={fetchJobDetails} />
        )}

        {activeTab === "changes" && jobActivity && (
          <JobChanges jobActivity={jobActivity} job={job} />
        )}
      </div>
    </div>
  );
};

export default ViewJob;
